import React from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import { Facebook } from "@mui/icons-material";
import { YouTube as YouTubeIcon, MusicNote as TikTokIcon } from "@mui/icons-material";
import SpotifyIcon from "../../components/icons/SpotifyIcon";
import type { InvitePlatform, InviteViewPhase } from "./inviteTypes";
import type { CompanyPodcastOption } from "./metaConnectUtils";
import MetaAssetMapping from "./MetaAssetMapping";
import type { MetaIgLinkedAsset, MetaMappingRow } from "./metaConnectUtils";
import { inviteLinkSx, inviteTextSx } from "./inviteTypography";
import { isInviteConnectLifecyclePhase } from "./invitePhaseUtils";

export interface InviteConnectActionsProps {
  platform: InvitePlatform;
  phase: InviteViewPhase;
  termsAccepted: boolean;
  onTermsChange: (accepted: boolean) => void;
  facebookLoading: boolean;
  facebookAccessToken: string | null;
  metaNeedsPageId: boolean;
  metaPageId: string;
  onMetaPageIdChange: (value: string) => void;
  metaSubmitting: boolean;
  companyPodcasts?: CompanyPodcastOption[];
  selectedPodcastIds: string[];
  metaAssets: MetaIgLinkedAsset[];
  metaMappings: MetaMappingRow[];
  onMetaMappingsChange: (mappings: MetaMappingRow[]) => void;
  onMetaContinueToFacebook: () => void;
  onMetaFinalize: () => void;
  onMetaMappingBack: () => void;
  spotifyUrl: string;
  onSpotifyUrlChange: (value: string) => void;
  spotifyLoading: boolean;
  onMetaConnect: () => void;
  onMetaPageIdSubmit: () => void;
  onYouTubeConnect: () => void;
  onTikTokConnect: () => void;
  onSpotifyConnect: () => void;
}

export const InviteConnectActions: React.FC<InviteConnectActionsProps> = ({
  platform,
  phase,
  termsAccepted,
  onTermsChange,
  facebookLoading,
  facebookAccessToken,
  metaNeedsPageId,
  metaPageId,
  onMetaPageIdChange,
  metaSubmitting,
  companyPodcasts = [],
  selectedPodcastIds,
  metaAssets,
  metaMappings,
  onMetaMappingsChange,
  onMetaContinueToFacebook,
  onMetaFinalize,
  onMetaMappingBack,
  spotifyUrl,
  onSpotifyUrlChange,
  spotifyLoading,
  onMetaConnect,
  onMetaPageIdSubmit,
  onYouTubeConnect,
  onTikTokConnect,
  onSpotifyConnect,
}) => {
  const showTerms = isInviteConnectLifecyclePhase(phase);

  const metaShowPageId =
    phase === "meta_page_id" || phase === "prixi_meta_page_id";

  const metaShowMapping =
    phase === "meta_mapping" || phase === "prixi_meta_mapping";

  const metaShowConnectButton =
    phase === "meta_podcast_select" ||
    phase === "prixi_meta_podcast_select" ||
    phase === "connect" ||
    phase === "connect_error" ||
    phase === "prixi_connect" ||
    phase === "prixi_connect_error";

  const selectedPodcasts = companyPodcasts.filter((p) =>
    selectedPodcastIds.includes(p.podcastId),
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", width: "100%", gap: 2 }}>
      {showTerms && !metaShowMapping && (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={termsAccepted}
                onChange={(e) => onTermsChange(e.target.checked)}
              />
            }
            label={
              <Typography variant="body2" component="span" sx={inviteTextSx}>
                Acepto los{" "}
                <Link
                  href="/legal/terminos"
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="always"
                  variant="body2"
                  sx={inviteLinkSx}
                >
                  Términos de Servicio
                </Link>{" "}
                y{" "}
                <Link
                  href="/legal/privacidad"
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="always"
                  variant="body2"
                  sx={inviteLinkSx}
                >
                  Política de Privacidad
                </Link>
              </Typography>
            }
          />
        </Box>
      )}

      <Box sx={{ position: "relative", width: "100%" }}>
        {platform === "meta" && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, width: "100%" }}>
            {metaShowMapping ? (
              <MetaAssetMapping
                assets={metaAssets}
                selectedPodcasts={selectedPodcasts}
                mappings={metaMappings}
                onMappingsChange={onMetaMappingsChange}
                submitting={metaSubmitting}
                onSubmit={onMetaFinalize}
                onBack={onMetaMappingBack}
              />
            ) : metaShowConnectButton && !metaShowPageId ? (
              <Button
                variant="contained"
                startIcon={
                  facebookLoading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <Facebook />
                  )
                }
                onClick={onMetaContinueToFacebook}
                disabled={
                  facebookLoading ||
                  !termsAccepted ||
                  selectedPodcastIds.length === 0
                }
                fullWidth
                sx={{
                  backgroundColor: "#1877F2",
                  color: "#fff",
                  "&:hover": { backgroundColor: "#166FE5" },
                }}
              >
                {facebookLoading
                  ? "Conectando..."
                  : "Continuar con Facebook"}
              </Button>
            ) : metaShowPageId ? (
              <>
                <TextField
                  label="Facebook Page ID"
                  placeholder="Ej: 123456789012345"
                  value={metaPageId}
                  onChange={(e) => onMetaPageIdChange(e.target.value)}
                  fullWidth
                  size="small"
                />
                <Button
                  variant="contained"
                  onClick={onMetaPageIdSubmit}
                  disabled={metaSubmitting || !metaPageId.trim() || !termsAccepted}
                  fullWidth
                  sx={{
                    backgroundColor: "#1877F2",
                    "&:hover": { backgroundColor: "#166FE5" },
                  }}
                >
                  {metaSubmitting ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    "Conectar Instagram con Page ID"
                  )}
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                startIcon={
                  facebookLoading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <Facebook />
                  )
                }
                onClick={onMetaConnect}
                disabled={facebookLoading || !termsAccepted}
                fullWidth
                sx={{
                  backgroundColor: "#1877F2",
                  color: "#fff",
                  "&:hover": { backgroundColor: "#166FE5" },
                }}
              >
                {facebookLoading
                  ? "Conectando..."
                  : "Conectar Instagram"}
              </Button>
            )}
          </Box>
        )}

        {platform === "youtube" && (
          <Button
            variant="contained"
            color="error"
            startIcon={<YouTubeIcon />}
            onClick={onYouTubeConnect}
            disabled={!termsAccepted}
            fullWidth
          >
            Conectar YouTube
          </Button>
        )}

        {platform === "tiktok" && (
          <Button
            variant="contained"
            startIcon={<TikTokIcon />}
            onClick={onTikTokConnect}
            disabled={!termsAccepted}
            fullWidth
            sx={{
              backgroundColor: "#010101",
              "&:hover": { backgroundColor: "#000000" },
            }}
          >
            Conectar TikTok
          </Button>
        )}

        {platform === "spotify" && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label="URL de Spotify"
              placeholder="https://open.spotify.com/show/..."
              value={spotifyUrl}
              onChange={(e) => onSpotifyUrlChange(e.target.value)}
              fullWidth
              size="small"
              helperText="Pega aquí la URL de tu podcast en Spotify"
            />
            <Button
              variant="contained"
              startIcon={
                spotifyLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <SpotifyIcon />
                )
              }
              onClick={onSpotifyConnect}
              disabled={spotifyLoading || !spotifyUrl.trim() || !termsAccepted}
              fullWidth
              sx={{
                backgroundColor: "#1DB954",
                "&:hover": { backgroundColor: "#1ed760" },
                "&:disabled": { backgroundColor: "#cccccc" },
              }}
            >
              {spotifyLoading ? "Conectando Spotify..." : "Conectar Spotify"}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default InviteConnectActions;
